import { useState, useEffect, useCallback } from "react";
import {
  Stack,
  Title,
  Group,
  Button,
  Table,
  TextInput,
  PasswordInput,
  Select,
  Switch,
  Modal,
  Badge,
  Pagination,
  Loader,
  Center,
  Text,
  ActionIcon,
  Avatar,
  ThemeIcon,
  Card,
} from "@mantine/core";
import { IconPlus, IconEdit, IconTrash, IconSearch, IconUsers } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import ConfirmModal from "@/components/ConfirmModal";

const ROLE_OPTIONS = [
  { value: "maintainer", label: "Maintainer" },
  { value: "developer", label: "Developer" },
  { value: "reporter", label: "Reporter" },
  { value: "guest", label: "Guest" },
];

const ROLE_COLORS: Record<string, string> = {
  maintainer: "violet",
  developer: "blue",
  reporter: "orange",
  guest: "gray",
};

const emptyForm = { nickname: "", username: "", password: "", role: "developer", isActive: true };

export default function UserList() {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.user.list({ page, limit: 20, search: search || undefined });
      setUsers(res.data);
      setTotalPages(res.pagination.totalPages);
      setTotal(res.pagination.total);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditId(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (u: any) => {
    setEditId(u._id);
    setForm({ nickname: u.nickname, username: u.username, password: "", role: u.role, isActive: u.isActive });
    setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editId) {
        const data: any = { nickname: form.nickname, role: form.role, isActive: form.isActive };
        if (form.password) data.password = form.password;
        await api.user.update(editId, data);
      } else {
        await api.user.create(form);
      }
      notifications.show({ title: "Амжилттай", message: editId ? "Шинэчлэгдлээ" : "Үүсгэгдлээ", color: "green" });
      setModalOpen(false);
      load();
    } catch (err: any) {
      notifications.show({ title: "Алдаа", message: err.message, color: "red" });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.user.remove(deleteId);
      notifications.show({ title: "Амжилттай", message: "Устгагдлаа", color: "green" });
      setDeleteId(null);
      load();
    } catch (err: any) {
      notifications.show({ title: "Алдаа", message: err.message, color: "red" });
    } finally { setDeleting(false); }
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={3}>Хэрэглэгчид</Title>
          <Badge variant="light" size="lg">{total}</Badge>
        </Group>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate} size="sm">Хэрэглэгч нэмэх</Button>
      </Group>

      <TextInput placeholder="Нэрээр хайх..." leftSection={<IconSearch size={16} />} value={search} onChange={(e) => { setSearch(e.currentTarget.value); setPage(1); }} w={300} size="sm" />

      {loading ? (
        <Center h={300}><Loader /></Center>
      ) : users.length === 0 ? (
        <Center h={300}>
          <Stack align="center" gap="xs">
            <ThemeIcon size={48} variant="light" color="gray" radius="xl"><IconUsers size={24} /></ThemeIcon>
            <Text size="sm" c="dimmed">Хэрэглэгч байхгүй</Text>
          </Stack>
        </Center>
      ) : (
        <Card shadow="xs" padding={0} radius="md" withBorder>
          <Table.ScrollContainer minWidth={700}>
            <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
              <Table.Thead>
                <Table.Tr style={{ backgroundColor: 'var(--app-table-header)' }}>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={50}>#</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Хэрэглэгч</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Эрх</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Идэвхтэй</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Огноо</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={100}>Үйлдэл</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {users.map((u, idx) => (
                  <Table.Tr key={u._id}>
                    <Table.Td c="dimmed" fz="sm">{(page - 1) * 20 + idx + 1}</Table.Td>
                    <Table.Td>
                      <Group gap="sm">
                        <Avatar size={36} radius="xl" color="blue" variant="light">
                          {u.nickname?.charAt(0)?.toUpperCase() || "?"}
                        </Avatar>
                        <div>
                          <Text size="sm" fw={500}>{u.nickname}</Text>
                          <Text size="xs" c="dimmed">{u.username}</Text>
                        </div>
                      </Group>
                    </Table.Td>
                    <Table.Td><Badge color={ROLE_COLORS[u.role] || "gray"} variant="light" size="sm">{u.role}</Badge></Table.Td>
                    <Table.Td><Badge color={u.isActive ? "green" : "gray"} variant="dot" size="sm">{u.isActive ? "Тийм" : "Үгүй"}</Badge></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{formatDate(u.createdAt)}</Text></Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <ActionIcon size="sm" variant="light" radius="md" color="blue" onClick={() => openEdit(u)}><IconEdit size={14} /></ActionIcon>
                        <ActionIcon size="sm" variant="light" radius="md" color="red" onClick={() => setDeleteId(u._id)}><IconTrash size={14} /></ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {totalPages > 1 && (
            <Group justify="center" p="md" style={{ borderTop: '1px solid var(--app-border)' }}>
              <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
            </Group>
          )}
        </Card>
      )}

      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={<Text fw={600}>{editId ? "Хэрэглэгч засах" : "Хэрэглэгч нэмэх"}</Text>} size="sm" centered>
        <Stack>
          <TextInput label="Нэр" placeholder="Nickname" value={form.nickname} onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, nickname: v })); }} required size="sm" />
          {!editId && <TextInput label="Нэвтрэх нэр" placeholder="Username" value={form.username} onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, username: v })); }} required size="sm" />}
          <PasswordInput label={editId ? "Шинэ нууц үг (хоосон бол өөрчлөхгүй)" : "Нууц үг"} placeholder="••••••" value={form.password} onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, password: v })); }} size="sm" />
          <Select label="Эрх" data={ROLE_OPTIONS} value={form.role} onChange={(v) => setForm((f) => ({ ...f, role: v || "developer" }))} size="sm" />
          <Switch label="Идэвхтэй" checked={form.isActive} onChange={(e) => { const v = e.currentTarget.checked; setForm((f) => ({ ...f, isActive: v })); }} />
          <Button onClick={handleSave} loading={saving} size="sm">Хадгалах</Button>
        </Stack>
      </Modal>

      <ConfirmModal opened={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} loading={deleting} />
    </Stack>
  );
}
